import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState } from "@/components/common";
import { api, fmtDateTime, ROLE_LABEL } from "@/lib/api";
import "@/ch.css";

const MSG_CLS = { sent: "text-emerald-700", mocked: "text-amber-700", failed: "text-red-600" };

function AuditTable() {
  const [items, setItems] = useState(null);
  const [who, setWho] = useState("all");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => api.get("/audit", { params: { limit: 300, who: who === "all" ? undefined : who, q: q.trim() || undefined } }).then((r) => setItems(r.data)).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [who, q]);
  const bar = (
    <div className="ch chs phd" style={{ marginBottom: 12 }}>
      <div className="seg2 ptabs" style={{ margin: "0 0 10px" }} data-testid="audit-who">{[["all", "Everything"], ["staff", "Staff actions"], ["client", "Client and system"]].map(([k, l]) => <span key={k} className={who === k ? "on" : ""} onClick={() => setWho(k)} data-testid={`audit-who-${k}`}>{l}</span>)}</div>
      <input className="sel" placeholder="Search the trail by action, email or record…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" />
    </div>);
  if (!items) return bar;
  if (!items.length) return <>{bar}<EmptyState title="No audit entries" testId="audit-empty" /></>;
  return (
    <>{bar}
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full min-w-[760px] text-sm" data-testid="audit-table">
        <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entity</th><th className="px-4 py-3">Details</th></tr></thead>
        <tbody>{items.map((a) => (
          <tr key={a.id} className="border-t align-top">
            <td className="whitespace-nowrap px-4 py-2.5 text-xs text-muted-foreground">{fmtDateTime(a.ts)}</td>
            <td className="px-4 py-2.5"><div className="text-xs font-medium">{a.actor_email}</div><div className="text-[11px] text-muted-foreground">{ROLE_LABEL[a.actor_role] || a.actor_role}</div></td>
            <td className="px-4 py-2.5 font-mono text-xs">{a.action}</td>
            <td className="px-4 py-2.5 text-xs">{a.entity_type}</td>
            <td className="max-w-md truncate px-4 py-2.5 font-mono text-[11px] text-muted-foreground" title={JSON.stringify(a.details)}>{JSON.stringify(a.details)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
    </>
  );
}

function MessageTable() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/messages").then((r) => setItems(r.data)); }, []);
  return (
    <div className="space-y-3">
      <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="whatsapp-mock-notice">Automatic WhatsApp delivery is not available. Rows marked "whatsapp_click · draft_opened" only mean a staff member opened WhatsApp with a prepared draft; they do not confirm the message was sent. Older "whatsapp · mocked" rows were simulations and were never delivered. Email "sent" means the email service accepted the message.</p>
      {!items.length ? <EmptyState title="No messages sent yet" testId="messages-empty" /> : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-[640px] text-sm" data-testid="messages-table">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Channel</th><th className="px-4 py-3">To</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">Status</th></tr></thead>
            <tbody>{items.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="whitespace-nowrap px-4 py-2.5 text-xs text-muted-foreground">{fmtDateTime(m.created_at)}</td>
                <td className="px-4 py-2.5 capitalize">{m.channel}</td>
                <td className="px-4 py-2.5 text-xs">{m.to}</td>
                <td className="max-w-sm truncate px-4 py-2.5 text-xs">{m.subject || m.body}</td>
                <td className={`px-4 py-2.5 text-xs font-semibold uppercase ${MSG_CLS[m.status]}`}>{m.status}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Audit() {
  return (
    <div>
      <PageHeader title="Audit trail" subtitle="Immutable record of every significant action. Entries cannot be edited or deleted." />
      <Tabs defaultValue="audit">
        <TabsList className="mb-4"><TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger><TabsTrigger value="messages" data-testid="tab-messages">Message log</TabsTrigger></TabsList>
        <TabsContent value="audit"><AuditTable /></TabsContent>
        <TabsContent value="messages"><MessageTable /></TabsContent>
      </Tabs>
    </div>
  );
}
